<template>
  <SogPage id="page">
    <SogCard class="sog-card-without-title">
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_vehicleRegistration?.insuranceId"
          mdi-icon="mdi-shield"
          tooltip="Versicherungskarte"
          @click="onPolicy"
        />
        <SogCardActionButton
          mdi-icon="mdi-paperclip-plus"
          tooltip="Anhang hinzufügen"
          @click="_uploadTempFile = true"
        />
        <SogCardActionButton
          mdi-icon="mdi-camera"
          tooltip="Anhang fotografieren"
          @click="onTakePhoto"
        />

        <SogCardActionButton
          v-show="!_vehicleRegistration?.insuranceId"
          mdi-icon="mdi-shield-link-variant"
          tooltip="Verknüpfen"
          @click="onOpen('link')"
        />
        <SogCardActionDropDownButton
          v-if="_isRoot"
          v-show="_vehicleRegistration?.id && !_vehicleRegistration.insuranceId && _vehicleRegistration.ignore !== 'Y'"
          mdi-icon="mdi-email-plus"
          :items="registrationInquiryMailTypeItems"
          @select="onCreateInquiry"
        />

        <SogCardActionButton
          v-show="_vehicleRegistration?.insuranceId"
          mdi-icon="mdi-shield-remove"
          tooltip="Verknüpfung löschen"
          @click="onOpen('unlink')"
        />
        <SogCardActionButton
          v-show="_vehicleRegistration?.ignore==='Y' || !_vehicleRegistration?.insuranceId"
          mdi-icon="mdi-close"
          tooltip="Ignorieren"
          @click="onOpen('ignore')"
        />
        <SogDialog
          ref="_dialogIgnore"
          target="#app"
          :dialog-buttons="SogDialogButtons.OkCancel"
          @click-ok="onOkIgnore"
          @click-cancel="onCancelLink('ignore')"
        >
          <SogTextBox
            v-model="_reason"
            :multiline="true"
            width="100%"
            placeholder="Grund fürs Ignorieren"
          />
        </SogDialog>

        <VehicleRegistrationLinkDialog
          ref="_dialogLink"
          target="#app"
          :vehicle-registration-id="_vehicleRegistration?.id"
          @linked="load"
        />

        <SogDialog
          ref="_dialogUnlink"
          target="#app"
          :dialog-buttons="SogDialogButtons.OkCancel"
          @click-ok="onOkUnLink"
          @click-cancel="onCancelLink('unlink')"
        >
          <SogTextBox
            v-model="_reason"
            :multiline="true"
            width="100%"
            placeholder="Grund fürs Löschen"
          />
        </SogDialog>
        <SogCardActionButton
          :kind="SogButtonKind.Back"
        />
      </SogCardActions>
      <SogCardContent v-show="!_loadDetails">
        <VehicleRegistrationComponent :data="_vehicleRegistration" />
      </SogCardContent>
      <SogCardContent>
        <SogSkeleton v-show="_loadDetails" />
      </SogCardContent>
    </SogCard>
    <VehicleRegistrationSuggestionComponent
      v-if="_isRoot && _vehicleRegistration?.id && !_vehicleRegistration.insuranceId && _vehicleRegistration.ignore !== 'Y'"
      :vehicle-registration-id="_vehicleRegistration.id"
      @changed="load"
    />
    <TempFileFormComponent
      v-show="_uploadTempFile"
      ref="_uploader"
      v-model="_tempFileInfo"
    >
      <SogCardActions>
        <SogCardActionButton
          v-show="_tempFileInfo"
          mdi-icon="mdi-upload"
          @click="onImport"
        >
          importieren
        </SogCardActionButton>
        <SogCardActionButton
          :kind="SogButtonKind.Cancel"
          @click="_uploadTempFile=false;_camera=false"
        />
      </SogCardActions>
    </TempFileFormComponent>
    <SogCard v-show="!_editMemoVisible">
      <SogCardTitle>Bemerkungen</SogCardTitle>
      <SogCardContent v-show="!_loadDetails">
        <SogFlexColumn>
          <MemoComponent
            :memo="_memos"
          />
        </SogFlexColumn>
      </SogCardContent>

      <SogCardContent>
        <SogSkeleton v-show="_loadDetails" />
      </SogCardContent>
      <SogCardActions>
        <SogCardActionButton
          mdi-icon="mdi-pencil"
          tooltip="Bearbeiten"
          @click="onEditMemo"
        />
      </SogCardActions>
    </SogCard>
    <SogCard v-show="_editMemoVisible">
      <SogCardContent>
        <MemoEditComponent
          v-show="_editMemoVisible"
          ref="_form"
          v-model="_memo"
        />
      </SogCardContent>
      <SogCardActions>
        <SogCardActionButton
          :kind="SogButtonKind.Save"
          @click="onSave"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Cancel"
          @click="onCancel"
        />
      </SogCardActions>
    </SogCard>
    <SogCard>
      <SogCardTitle>Gdv Nachrichten</SogCardTitle>
      <SogCardContent v-show="!_loadDetails">
        <SogFlexColumn>
          <GdvMessageIdComponent
            v-for="(data,i) in _gdvMessageIds"
            :key="i"
            :item="data"
          />
        </SogFlexColumn>
      </SogCardContent>
      <SogCardContent>
        <SogSkeleton v-show="_loadDetails" />
      </SogCardContent>
    </SogCard>
    <SogCard v-show="_loadHistory || _history.length>0">
      <SogCardTitle>Historie</SogCardTitle>
      <SogCardContent v-show="!_loadHistory">
        <HistoryListComponent :data="_history" />
      </SogCardContent>
      <SogCardContent>
        <SogSkeleton v-show="_loadHistory" />
      </SogCardContent>
    </SogCard>
    <SogCard v-show="_loadFiles || _tempFile.length!==0">
      <SogCardTitle>Anhänge</SogCardTitle>
      <SogCardContent
        v-for="file in _tempFile"
        v-show="!_loadFiles"
        :key="file.id"
      >
        <FileCardComponent
          :file="file"
        >
          <SogCardActionButton
            mdi-icon="mdi-delete"
            tooltip="Löschen"
            @click="onDelete(file)"
          />
          <SogCardActionButton
            mdi-icon="mdi-download"
            tooltip="Download"
            @click="onDownload(file)"
          />
        </FileCardComponent>
      </SogCardContent>
      <SogCardContent>
        <SogSkeleton v-show="_loadFiles" />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">

import SogPage from "@/shared/ui/SogPage.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import VehicleRegistrationComponent
  from "@/modules/VehicleRegistration/components/VehicleRegistrationFullComponent.vue";
import {onMounted, reactive, ref} from "vue";
import {storeToRefs} from "pinia";
import {useAccountStore} from "@/stores/accountStore";
import apiVehicleRegistration from "@/modules/VehicleRegistration/api";
import GdvMessageIdComponent from "@/modules/VehicleRegistration/components/GdvMessageIdComponent.vue";
import VehicleRegistrationSuggestionComponent
  from "@/modules/VehicleRegistration/components/VehicleRegistrationSuggestionComponent.vue";
import SogCardActionDropDownButton from "@/shared/ui/SogCardActionDropDownButton.vue";
import SogDropDownButtonItem from "@/shared/ui/types/SogDropDownButtonItem";
import apiRegistrationInquiry from "@/modules/RegistrationInquiry/api";
import {registrationInquiryMailTypeItems} from "@/modules/RegistrationInquiry/mailTypeItems";
import SogFlexColumn from "@/shared/ui/SogFlexColumn.vue";
import MemoComponent from "@/modules/VehicleRegistration/components/MemoComponent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import notificationService from "@/shared/services/NotificationService";
import TempFileFormComponent from "@/modules/FileUtils/components/TempFileFormComponent.vue";
import ListFile from "@/shared/model/ListFile";
import FileCardComponent from "@/modules/FileUtils/components/FileCardComponent.vue";
import HistoryListComponent from "@/modules/History/components/HistoryListComponent.vue";
import History from "@/shared/model/History";
import apiHistory from "@/modules/History/api";
import apiFileUtils from "@/modules/FileUtils/api";
import apiFile from "@/shared/services/apiFile";
import {useSpinner} from "@/shared/composables/SogSpinner";
import SogSkeleton from "@/shared/ui/SogSkeleton.vue";
import getGuid from "@/shared/utils/getGuid";
import VehicleRegistration from "@/shared/model/VehicleRegistration";
import {useNavigationStore} from "@/modules/Navigation/store";
import MemoEditComponent from "@/modules/VehicleRegistration/components/MemoEditComponent.vue";
import SogDialog from "@/shared/ui/SogDialog.vue";
import {SogDialogButtons} from "@/shared/ui/types/SogDialogButtons";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import VehicleRegistrationLinkDialog
  from "@/modules/VehicleRegistration/components/VehicleRegistrationLinkDialog.vue";

interface Props {
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
    id: undefined
  }
)

const _navigationStore = useNavigationStore()
const {isRoot: _isRoot} = storeToRefs(useAccountStore())
const vehicleRegistrationId = ref<string | undefined>(undefined)
const _gdvMessageIds = ref<string[]>([])
const _memos = ref<string>("")
const _history: History[] = reactive<History[]>([])
const _vehicleRegistration = ref<VehicleRegistration | undefined>(undefined)
const _form = ref<InstanceType<typeof MemoEditComponent> | null>(null)
const _loadDetails = ref(true)
const _loadFiles = ref(true)
const _loadHistory = ref(true)
const _key = ref("")
const _editMemoVisible = ref(false)
const _tempFileInfo = ref<{ id?: string, fileName?: string, name?: string } | undefined>(undefined)
const _uploadTempFile = ref(false)
const _uploader = ref<InstanceType<typeof TempFileFormComponent> | null>(null)
const _camera = ref(false)
const _tempFile = ref<ListFile[]>([])
const _spinner = useSpinner()
const _memo = ref<string | undefined>(undefined)
const _reason = ref<string | undefined>("")
const _dialogUnlink = ref<InstanceType<typeof SogDialog> | null>(null)

async function load() {

  if (!_navigationStore.currentVehicleRegistrationId && !props.id) {
    return
  }
  vehicleRegistrationId.value = props.id ?? _navigationStore.currentVehicleRegistrationId

  _key.value = getGuid();
  const t_loadDetails = loadDetails(_key.value);
  const t_loadFiles = loadFiles(_key.value);
  const t_loadHistory = loadHistory(_key.value);

  await Promise.allSettled([t_loadDetails, t_loadFiles, t_loadHistory])
}

async function loadFiles(key: string) {
  if (!vehicleRegistrationId.value) {
    return
  }
  _loadFiles.value = true
  try {

    const listFile = await apiFileUtils.listFileStore(vehicleRegistrationId.value)

    if (_key.value !== key) {
      return
    }
    _tempFile.value = listFile?.sort((a, b) => (a.date && b.date) ? Date.parse(b.date.toString()) - Date.parse(a.date.toString()) : 0) ?? [];
  } finally {
    _loadFiles.value = false
  }
}

async function loadHistory(key: string) {
  if (!vehicleRegistrationId.value)
    return
  _loadHistory.value = true
  _history.length = 0
  const lst = await apiHistory.listVehicleRegistration(vehicleRegistrationId.value)
  if (_key.value !== key) {
    return
  }

  if (lst) {
    _history.push(...lst)
  }
  _loadHistory.value = false
}


async function loadDetails(key: string) {
  if (!vehicleRegistrationId.value)
    return
  _loadDetails.value = true
  //await delay(4000)
  const res = await apiVehicleRegistration.load(vehicleRegistrationId.value)
  if (_key.value !== key) {
    return
  }
  if (res) {
    _vehicleRegistration.value = res.vehicleRegistration
    _gdvMessageIds.value = res?.vehicleRegistration.gdvMessageIds?.split('|').filter(Boolean) ?? []
    _memos.value = res?.vehicleRegistration.memo ?? ""
  }
  _loadDetails.value = false
}


onMounted(async () => {
  await load();
})

async function onImport() {
  if (!_tempFileInfo.value || !_tempFileInfo.value.id)
    return;
  const res = await apiVehicleRegistration.addTempFile(_tempFileInfo.value.id, vehicleRegistrationId.value, _tempFileInfo.value.name, _tempFileInfo.value.fileName);
  if (res) {
    notificationService.success("Datei " + _tempFileInfo.value.name + " wurde gespeichert")
    _tempFileInfo.value = undefined
    _uploader.value?.clear()
    _uploadTempFile.value = false
    await load()
  }
}

function onTakePhoto() {
  _uploadTempFile.value = true;
  _camera.value = true
  _uploader.value?.takePhoto()
}

function onDelete(file: ListFile) {

  notificationService.showYesNo("Datei löschen", "Soll die Datei " + file.name + " wirklich gelöscht werden?", async () => {
    const res = await apiFileUtils.deleteFile(file?.id ?? "")
    if (res) {
      notificationService.success("Datei " + file.name + " wurde gelöscht")
      _tempFile.value = _tempFile.value.filter(f => f.id !== file.id)
    } else
      notificationService.error("Datei " + file.name + " konnte nicht gelöscht werden")
  })

}

async function onDownload(file: ListFile) {
  if (!file.personId)
    return;
  try {
    _spinner.show()
    const fileId = await apiFileUtils.initDownloadFile(file.id ?? "");
    if (fileId)
      await apiFile.downloadTempFile(fileId)

  } finally {
    _spinner.hide()
  }
}

async function onSave() {
  if (!_form.value?.validate()) {
    return
  }
  const obj = new VehicleRegistration();
  obj.id = _vehicleRegistration.value?.id
  obj.memo = _memo.value
  //obj.halterName = _vehicleRegistration.value?.halterName+"!!"


  const res = await apiVehicleRegistration.update(obj, "Memo")
  if (res) {
    notificationService.success("Bemerkung wurde gespeichert")
    await load()
    _editMemoVisible.value = false
  }
}

const _dialogLink = ref<InstanceType<typeof VehicleRegistrationLinkDialog> | null>(null)
const _dialogIgnore = ref<InstanceType<typeof SogDialog> | null>(null)

function onOpen(action: string) {
  if (action === "link")
    _dialogLink.value?.show()
  else if (action === "unlink")
    _dialogUnlink.value?.show()
  else
    _dialogIgnore.value?.show()
}

function onCancelLink(action: string) {
  if (action === "unlink") {
    _dialogUnlink.value?.hide()
    _reason.value = ""
  } else {
    _dialogIgnore.value?.hide()
    _reason.value = ""
  }

}

async function onOkUnLink() {

  if (!_reason.value) {
    notificationService.error("Bitte einen Grund eingeben")
    return
  }
  const res = await apiVehicleRegistration.insuranceLink(_vehicleRegistration.value?.id ?? "", _vehicleRegistration.value?.id ?? "", _reason.value, true)
  if (res) {
    notificationService.success("Zulassung erfolgreich unverknüpft")
    _reason.value = ""
    _dialogUnlink.value?.hide()
    await load()

  }
}

async function onOkIgnore() {

  if (!_reason.value) {
    notificationService.error("Bitte einen Grund eingeben")
    return
  }
  const res = await apiVehicleRegistration.ignore(_vehicleRegistration.value?.id ?? "", _reason.value)
  if (res) {
    notificationService.success("Zulassung erfolgreich ignoriert")
    _reason.value = ""
    _dialogIgnore.value?.hide()
    await load()

  }

}

async function onCreateInquiry(item?: SogDropDownButtonItem) {
  if (!item?.id || !_vehicleRegistration.value?.id) {
    return
  }
  const t_id = await apiRegistrationInquiry.create(_vehicleRegistration.value.id, item.id)
  if (t_id) {
    notificationService.success("Entwurf wurde angelegt und liegt unter Recherche-Mails")
    await load()
  }
}

function onPolicy() {
  if (_vehicleRegistration.value?.insuranceId) {
    _navigationStore.currentInsuranceId = _vehicleRegistration.value.insuranceId
  }
}


function onCancel() {
  _editMemoVisible.value = false

}

function onEditMemo() {
  _memo.value = _vehicleRegistration.value?.memo
  _editMemoVisible.value = true
}
</script>

<style scoped>

</style>
